/**
 * React hooks and components for tracking portal section views
 *
 * @example Component usage (declarative)
 * <PortalSectionViewTracker section="Your Section" subSection="Your Sub Section" providerList={['123', '456']}>
 *   <Your_Monitored_Component />
 * </PortalSectionViewTracker>
 *
 * @example Hook usage
 * const trackingRef = useTrackPortalSectionView({
 *   section: 'Account',
 *   subSection: 'Login',
 *   providerList: ['123', '456']
 * });
 * return <div ref={trackingRef}>Login Form</div>;
 *
 * @note authenticationLevel is automatically determined from window.utag_data
 * and doesn't need to be passed explicitly. You can override it by passing
 * an explicit authenticationLevel prop if needed.
 */

import React, {
  createContext,
  useContext,
  useDebugValue,
  useEffect,
  useRef,
} from 'react';
import { useInView } from 'react-intersection-observer';

import { useCohesion } from '../../CohesionProvider';
import PortalSectionViewedEvent from './index';

const { createEventPayload } = PortalSectionViewedEvent;

/**
 * @typedef {import('zod').infer<
 *   typeof import('./index').default.ReactComponentInputSchema
 * >} PortalSectionViewedEventSchema
 */

// NOTE: Deduplication is handled by CohesionProvider (deduplication: { scope: 'page' } in event definition)

/**
 * Determines the authentication level with proper fallback logic
 * @returns {string} The authentication level
 */
function getAuthenticationLevel() {
  // Try UserRoleName first (more specific), then UserRole, then fallback to 'user'
  return (
    window?.utag_data?.UserRoleName || window?.utag_data?.UserRole || 'provider'
  );
}

/**
 * Tracks when a section of the portal is viewed by the user.
 * @param {PortalSectionViewedEventSchema} eventData - Event data
 * @returns {React.RefCallback<HTMLDivElement>} The ref to attach to your element to observe its visibility
 */
export function useTrackPortalSectionView(eventData) {
  const [ref, inView, entry] = useInView({
    triggerOnce: true,
    threshold: 0.1, // 0.1 = 10% of the element must be in the viewport to consider the element "in-view"
  });

  const { trackEvent } = useCohesion();

  // Store latest event data in ref to avoid stale closures
  const latestEventDataRef = useRef(eventData);
  useEffect(() => {
    latestEventDataRef.current = eventData;
  });

  useEffect(() => {
    if (!inView) return;

    trackEvent(
      createEventPayload({
        authenticationLevel: getAuthenticationLevel(),
        ...latestEventDataRef.current,
      }),
    );
  }, [inView, trackEvent]);

  useDebugValue(
    {
      inView,
      isObserving: !!entry,
    },
    ({ inView, isObserving }) => {
      if (!isObserving) {
        return 'Not observing → Ref not attached (no children)';
      }
      return inView
        ? 'Visible → Tracking handled by CohesionProvider'
        : 'Not visible → Waiting';
    },
  );

  return ref;
}

/**
 * @typedef {{ section: string, subSection: string }} PortalSectionContextValue
 */

const PortalSectionContext = createContext(
  /** @type {PortalSectionContextValue | undefined} */ (undefined),
);

/**
 * @param {PortalSectionContextValue & {
 *   children: React.ReactNode,
 * }} props
 */
export function PortalSectionProvider({
  section,
  subSection,
  providerList,
  children,
}) {
  const sectionContext = React.useMemo(
    () => ({ section, subSection, providerList }),
    [section, subSection, providerList],
  );

  return (
    <PortalSectionContext.Provider value={sectionContext}>
      {children}
    </PortalSectionContext.Provider>
  );
}

export function usePortalSection() {
  const context = useContext(PortalSectionContext);

  if (!context) {
    throw new Error(
      'usePortalSection must be used within a PortalSectionProvider',
    );
  }

  return context;
}

/**
 * Component wrapper for tracking in Cohesion when `children` is in view
 *
 * @param {PortalSectionViewedEventSchema & {
 *   as?: React.ElementType, // Component to render as wrapper
 *   children: React.ReactNode,
 * }} props
 */
export function PortalSectionViewTracker({
  children,
  as: Element = 'div',
  ...trackingProps
}) {
  const ref = useTrackPortalSectionView(trackingProps);

  const { section, subSection, providerList } = trackingProps;

  return (
    <PortalSectionProvider
      section={section}
      subSection={subSection}
      providerList={providerList}
    >
      <Element
        data-testid="portal-section-view-tracker"
        /**
         * If `children` is not provided, the ref will not be attached to the element
         * and the "in view" state will not be tracked.
         * This is useful for conditionally-rendered element like Modals
         */
        ref={children ? ref : null}
      >
        {children}
      </Element>
    </PortalSectionProvider>
  );
}
